import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withPreloading } from '@angular/router';
import { provideDatadogRouter } from '@datadog/browser-rum-angular';
import { environment } from '@env/environment';
import { provideTranslateService } from '@ngx-translate/core';
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { provideToastr } from 'ngx-toastr';

import { appRoutes } from './app.routes';
import { provideHypecastCore } from './core/core.providers';
import { CustomPreloadingStrategy } from './core/custom-preloading.strategy';
import { DatadogErrorHandler } from './core/datadog-error-handler';
import { apiPrefixInterceptor } from './core/http/api-prefix.interceptor';
import { authorizationInterceptorFn } from './core/http/authorization-functional.interceptor';
import { cacheInterceptorFn } from './core/http/cache-functional.interceptor';
import { errorHandlerInterceptor } from './core/http/error-handler.interceptor';
import { oauthAccessTokenInterceptor } from './core/http/oauth-access-token.interceptor';
import { planLimitInterceptor } from './core/http/plan-limit.interceptor';
import { tenantLifecycleInterceptor } from './core/http/tenant-lifecycle.interceptor';
import { I18nService, Language } from './core/i18n.service';
import { SessionReplayService } from './core/session-replay.service';
import {
  DayjsDateAdapter,
  DAYJS_DATE_FORMATS,
  MAT_DAYJS_DATE_ADAPTER_OPTIONS,
} from './shared/adapters/dayjs-date-adapter';
import { AppRoute } from './shared/app.route.enum';
import { provideHypecastAuthentication } from './shared/authentication/authentication.providers';
import { HypecastBaseOAuthConfig } from './shared/authentication/models/HypecastBaseOAuthConfig';
import { HYPECAST_OAUTH_CONFIG } from './shared/authentication/tokens/hypecast-oauth-config.token';
import { APP_ROUTE, APP_TITLE, BreadcrumbsService } from './shared/components/breadcrumbs/breadcrumbs.service';
import { ITenantService } from './shared/services/itenant.service';
import { LoadingBarService } from './shared/services/local/loading-bar/loading-bar.service';
import { rememberActivationDeepLink } from './shared/tenant-lifecycle/activation/activation-redirect';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

const maskConfig: Partial<NgxMaskConfig> = {
  validation: true,
};

const getCurrentLanguage = (i18nService: I18nService): string => {
  i18nService.init(environment.supportedLanguages);
  const language = i18nService.language;
  return typeof language === 'string'
    ? language
    : ((language as Language | undefined)?.locale ?? environment.defaultLanguage.locale);
};

const getOAuthConfig = (_tenantService: ITenantService): HypecastBaseOAuthConfig => new HypecastBaseOAuthConfig();

/**
 * Root application providers.
 *
 * The standalone application shell uses provider functions throughout. HTTP
 * is configured through native provideHttpClient(). Angular's default XHR
 * backend is retained because several Admin Console upload flows rely on
 * progress events that the fetch backend does not expose.
 *
 * Zoneless change detection is used. Components notify Angular through signals,
 * template events, async bindings, or ChangeDetectorRef.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes, withPreloading(CustomPreloadingStrategy)),
    // ngx-charts still ships legacy Angular animation triggers. Keep their
    // renderer lazy while all application-owned animations use native CSS.
    provideAnimationsAsync(),
    provideTranslateService(),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    // Before the login redirect: ?activate=1 survives the way through the identity provider.
    provideAppInitializer(() => rememberActivationDeepLink(window.location.search)),
    provideHypecastAuthentication(),
    LoadingBarService,
    provideHypecastCore(),
    provideHttpClient(
      withXhr(),
      withInterceptors([
        tenantLifecycleInterceptor,
        planLimitInterceptor,
        apiPrefixInterceptor,
        errorHandlerInterceptor,
        oauthAccessTokenInterceptor,
        authorizationInterceptorFn,
        cacheInterceptorFn,
      ]),
    ),
    BreadcrumbsService,
    CurrencyPipe,
    { provide: MAT_DATE_FORMATS, useValue: DAYJS_DATE_FORMATS },
    { provide: APP_ROUTE, useValue: AppRoute },
    { provide: APP_TITLE, useValue: 'Hypecast' },
    { provide: LOCALE_ID, useFactory: getCurrentLanguage, deps: [I18nService] },
    { provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DAYJS_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: HYPECAST_OAUTH_CONFIG, useFactory: getOAuthConfig, deps: [ITenantService] },
    { provide: ErrorHandler, useClass: DatadogErrorHandler },
    provideDatadogRouter(),
    // Session Replay is started manually (see initDatadogMonitoring); this hook
    // is what begins watching navigation so the recorded sections switch it on.
    provideAppInitializer(() => inject(SessionReplayService).start()),
    provideEnvironmentNgxMask(maskConfig),
  ],
};
